import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, withFormik } from 'formik';
import { isEmpty, map, uniqueId } from 'lodash';
import Yup from 'yup';
import { getFormValue } from '../../../util/helpers';
import { optionalNumberSchema } from '../../../util/validation';
import { FormLayout, renderFormSection } from '../../common';
import Medication from './Medication';
import {
  ACTIVE_NUMERATOR,
  ACTIVE_NUMERATOR_UNIT,
  DATE_PRESCRIBED,
  DOSAGE,
  FREQUENCY,
  FREQUENCY_UNIT,
  IS_SEARCH_RESULT,
  MEDICATION_TYPES,
  PRESCRIBING_DOCTOR_ID,
  MEDICATION_NAME,
  PHARMACY_ID,
  TYPE,
  PRN,
  CUSTOM_NAME,
  ALTERNATE_NAME,
  MEDICATION_NOTES,
} from '../constants';

const MEDICATIONS = 'medications';

const getNewMedication = () => ({
  [ACTIVE_NUMERATOR]: 0,
  [ACTIVE_NUMERATOR_UNIT]: '',
  [CUSTOM_NAME]: '',
  [DATE_PRESCRIBED]: '',
  [DOSAGE]: 0,
  [FREQUENCY]: 0,
  [FREQUENCY_UNIT]: '',
  [IS_SEARCH_RESULT]: false,
  [MEDICATION_NAME]: undefined,
  [PRESCRIBING_DOCTOR_ID]: undefined,
  [PHARMACY_ID]: undefined,
  [PRN]: false,
  [MEDICATION_NOTES]: undefined,
  tempId: uniqueId('temp-'),
  type: '',
});

function MedicationForm(props) {
  return (
    <FormLayout {...props}>
      <FieldArray
        name={MEDICATIONS}
        render={fieldArray =>
          renderFormSection({
            fieldArray,
            props: {
              ...props,
              component: Medication,
              onAddSection: getNewMedication,
              title: 'Medication',
            },
          })
        }
      />
    </FormLayout>
  );
}

MedicationForm.propTypes = {
  data: PropTypes.array,
  doctors: PropTypes.array,
  pharmacies: PropTypes.array,
};

MedicationForm.defaultProps = {
  data: [],
  doctors: [],
  pharmacies: [],
};

const toMedicationValues = medication => ({
  id: medication.id,
  tempId: medication.tempId,
  [ACTIVE_NUMERATOR]: getFormValue(medication, 'activeNumerator', ''),
  [ACTIVE_NUMERATOR_UNIT]: medication.activeNumeratorUnit,
  [ALTERNATE_NAME]: getFormValue(medication, 'alternateName', ''),
  [CUSTOM_NAME]: getFormValue(medication, 'customName', ''),
  [DATE_PRESCRIBED]: getFormValue(medication, 'datePrescribed', ''),
  [DOSAGE]: getFormValue(medication, 'dosage', ''),
  [FREQUENCY]: getFormValue(medication, 'frequency', ''),
  [FREQUENCY_UNIT]: medication.frequencyUnit,
  [IS_SEARCH_RESULT]: getFormValue(medication, 'isSearchResult', false),
  [PRESCRIBING_DOCTOR_ID]: getFormValue(medication, 'prescribingDoctorId', ''),
  [PHARMACY_ID]: getFormValue(medication, 'pharmacyId', ''),
  [PRN]: getFormValue(medication, 'prn', false),
  recurrenceId: medication.recurrenceId,
  [MEDICATION_NAME]: medication[MEDICATION_NAME],
  [MEDICATION_NOTES]: getFormValue(medication, MEDICATION_NOTES, ''),
  [TYPE]: getFormValue(medication, 'type', ''),
  routeId: getFormValue(medication, 'routeId', ''),
});

export default withFormik({
  displayName: 'Medications',
  handleSubmit: (values, { props, setStatus }) => {
    return props
      .onSave(
        values.medications.map(m => {
          if (!m.isSearchResult) m[MEDICATION_NAME] = null;
          else m[CUSTOM_NAME] = null;
          return m;
        }),
      )
      .then(() => (props.isEditing ? props.onCancel() : props.onContinue()))
      .catch(error => setStatus({ saveError: error }));
  },
  mapPropsToValues: ({ data }) => {
    return {
      medications: isEmpty(data)
        ? [getNewMedication()]
        : map(data, toMedicationValues),
    };
  },
  validationSchema: Yup.object().shape({
    medications: Yup.array().of(
      Yup.object().shape({
        [ACTIVE_NUMERATOR]: optionalNumberSchema.when(
          'type',
          (type, schema) => {
            if (type !== MEDICATION_TYPES.IV) {
              return schema
                .typeError('Value must be a number.')
                .min(0, 'Value cannot be negative.');
            }
            return schema;
          },
        ),
        [CUSTOM_NAME]: Yup.string().when(
          IS_SEARCH_RESULT,
          (isSearchResult, schema) =>
            !isSearchResult
              ? schema.required('Custom medication name is required.')
              : schema,
        ),
        [DOSAGE]: optionalNumberSchema.when('type', (type, schema) => {
          if (
            type === MEDICATION_TYPES.PILL ||
            type === MEDICATION_TYPES.LIQUID
          ) {
            return schema
              .typeError('Dosage must be a number.')
              .min(0, 'Dosage cannot be negative.');
          }

          return schema;
        }),
        [FREQUENCY]: optionalNumberSchema
          .typeError('Frequency must be a number.')
          .min(0, 'Value must be zero or greater'),
        [IS_SEARCH_RESULT]: Yup.boolean(),
        [MEDICATION_NAME]: Yup.object()
          .nullable()
          .when(IS_SEARCH_RESULT, (isSearchResult, schema) =>
            isSearchResult
              ? schema.required('Medication name is required.')
              : schema.notRequired(),
          ),
        [TYPE]: Yup.string(),
      }),
    ),
  }),
})(MedicationForm);



// WEBPACK FOOTER //
// ./src/modules/MedicationPage/components/MedicationForm.js